import React, { useEffect, useRef, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { RefreshCw, AlertTriangle, Database, Clock } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { formatNum } from "@/lib/api";

const StatusDot = ({ tone }) => {
  const map = {
    live: "bg-emerald-500",
    syncing: "bg-amber-500",
    error: "bg-rose-500",
    idle: "bg-slate-400",
  };
  return (
    <span className="relative inline-flex w-2.5 h-2.5">
      {tone === "live" && (
        <span className="absolute inset-0 rounded-full bg-emerald-500/60 animate-ping" />
      )}
      <span
        className={`relative inline-flex w-2.5 h-2.5 rounded-full ${
          map[tone] || map.idle
        }`}
      />
    </span>
  );
};

const computeStatusTone = (syncing, lastSyncDate, status) => {
  if (syncing) return { tone: "syncing", label: "Syncing…" };
  if (status === "error") return { tone: "error", label: "Sync error" };
  if (lastSyncDate) {
    return {
      tone: "live",
      label: `Live · ${formatDistanceToNow(lastSyncDate, { addSuffix: true })}`,
    };
  }
  return { tone: "idle", label: "Awaiting first sync" };
};

const renderStatusGlyph = (tone, syncing) => {
  if (tone === "error") {
    return (
      <AlertTriangle
        className="w-3.5 h-3.5 text-rose-500"
        data-testid="sync-error-icon"
      />
    );
  }
  if (syncing) {
    return <RefreshCw className="w-3.5 h-3.5 animate-spin text-[#1F3A5F]" />;
  }
  return <StatusDot tone={tone} />;
};

const popoverHeading = (tone, syncing) => {
  if (tone === "error") return "Last sync failed";
  if (syncing) return "Syncing now…";
  return "Live from Airtable";
};

export const SyncStatusIndicator = ({
  syncing,
  meta,
  autoSync,
  totalRecords,
  onSync,
}) => {
  // refresh "now" every 30s for the relative time string
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Open on hover (desktop) AND click — but never trigger sync from the trigger.
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const lastSyncDate = meta?.finished_at ? new Date(meta.finished_at) : null;
  const status = meta?.status || (lastSyncDate ? "ok" : "idle");
  const error = meta?.error;
  const source = meta?.source || "manual";

  const { tone, label } = computeStatusTone(syncing, lastSyncDate, status);
  const lastSyncAtFull = lastSyncDate
    ? format(lastSyncDate, "d MMM yyyy, HH:mm")
    : "—";

  const handleRefresh = async () => {
    if (syncing) return;
    await onSync();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="sync-button"
          aria-label="Show Airtable sync status"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          onFocus={openNow}
          onBlur={closeSoon}
          className="group inline-flex items-center gap-2 h-9 pl-2 pr-3 rounded-full border border-border bg-background/70 hover:bg-secondary/60 transition-colors"
        >
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-secondary/60 group-hover:bg-secondary">
            {renderStatusGlyph(tone, syncing)}
          </span>
          <span
            className="text-[11px] font-medium tabular text-muted-foreground"
            data-testid="sync-label"
          >
            {label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={6}
        className="w-[280px] p-3 text-xs leading-relaxed"
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="sync-popover"
      >
        <div className="font-semibold mb-2 flex items-center gap-1.5">
          <StatusDot tone={tone} />
          {popoverHeading(tone, syncing)}
        </div>
        <div className="space-y-1.5 text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Database className="w-3 h-3" />
            <span className="tabular">
              {formatNum(totalRecords)} records cached
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            <span className="tabular">
              Last sync · {lastSyncAtFull}
              {meta?.mode && (
                <span
                  className="ml-1.5 inline-flex items-center px-1.5 py-[1px] rounded text-[9px] font-semibold uppercase tracking-wide border border-border/60 bg-secondary/40 text-muted-foreground"
                  data-testid="sync-mode-tag"
                  title={
                    meta.mode === "incremental"
                      ? "Only records modified since last sync were fetched (~90% payload reduction)"
                      : "All records re-fetched. Full syncs run on demand + every 24h to catch deletions."
                  }
                >
                  {meta.mode}
                </span>
              )}
              {source === "auto" && " (auto)"}
            </span>
          </div>
          {autoSync?.enabled && (
            <div className="text-[11px]">
              Auto-sync every {autoSync.interval_minutes ?? 60} min ·{" "}
              {autoSync.window_local || "08:00–20:00 IST"}
            </div>
          )}
          {error && (
            <div className="text-[11px] text-rose-500 break-words">
              {error}
            </div>
          )}
        </div>
        <div className="mt-3 pt-3 border-t border-border/60">
          <Button
            type="button"
            size="sm"
            onClick={handleRefresh}
            disabled={syncing}
            data-testid="sync-refresh-button"
            className="w-full h-8 text-xs gap-2 bg-[#1F3A5F] hover:bg-[#15263F] text-white"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`}
            />
            {syncing ? "Syncing…" : "Refresh now"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
